<template>
	<WaitDialog :isActive="loading" title="Loading..." />

	<v-container fluid>
		<!-- Navigation Selectors -->
		<v-row>
			<v-col cols="12">
				<v-card class="text-left">
					<v-card-text>
						<v-row>
							<v-col cols="12" md="3">
								<v-select
									v-model="selectedCompany"
									:items="companies"
									:item-title="companyDisplay"
									:item-value="item => item"
									label="Company"
									variant="outlined"
									density="comfortable"
									hide-details
									:loading="loadingCompanies"
									@update:modelValue="onCompanyChange"
								/>
							</v-col>
							<v-col cols="12" md="2">
								<v-select
									v-model="selectedState"
									:items="states"
									item-title="stateCode"
									item-value="stateCode"
									label="State"
									variant="outlined"
									density="comfortable"
									hide-details
									:disabled="!selectedCompany"
									:loading="loadingStates"
									@update:modelValue="onStateChange"
								/>
							</v-col>
							<v-col cols="12" md="2">
								<v-select
									v-model="selectedLob"
									:items="lobs"
									item-title="lobCode"
									item-value="lobCode"
									label="Line of Business"
									variant="outlined"
									density="comfortable"
									hide-details
									:disabled="!selectedState"
									:loading="loadingLobs"
									@update:modelValue="onLobChange"
								/>
							</v-col>
							<v-col cols="12" md="2">
								<v-select
									v-model="selectedIssueCode"
									:items="issueCodes"
									item-title="description"
									item-value="code"
									label="Issue Code"
									variant="outlined"
									density="comfortable"
									hide-details
									:disabled="!selectedLob"
								/>
							</v-col>
							<v-col cols="12" md="3" class="d-flex align-center">
								<m-btn color="primary" prepend-icon="mdi-magnify" :disabled="!canSearch" @click="searchHovers">
									Search Hovers
								</m-btn>
								<m-btn class="ml-2" color="secondary" prepend-icon="mdi-plus" :disabled="!canSearch" @click="showAddDialog = true">
									Add Hover
								</m-btn>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<!-- Hovers Grid -->
		<v-row class="mt-2" v-if="hovers.length > 0 || userHovers.length > 0 || hasSearched">
			<v-col cols="12">
				<v-tabs v-model="activeHoverTab" color="primary" class="mb-0">
					<v-tab value="default">Default Hovers</v-tab>
					<v-tab value="user">User Hovers</v-tab>
				</v-tabs>
				<v-card class="text-left">
					<v-tabs-window v-model="activeHoverTab">
						<v-tabs-window-item value="default">
							<v-data-table
								:headers="hoverHeaders"
								:items="hovers"
								:loading="loading"
								density="comfortable"
								class="elevation-0"
								no-data-text="No hovers found"
								items-per-page="25"
								hover
							>
								<template #top>
									<v-toolbar flat>
										<v-toolbar-title>Default Hovers</v-toolbar-title>
										<v-divider class="mx-4" inset vertical />
										<v-chip class="mr-2">{{ hovers.length }} records</v-chip>
										<m-btn color="primary" prepend-icon="mdi-refresh" @click="searchHovers">Refresh</m-btn>
									</v-toolbar>
								</template>
							</v-data-table>
						</v-tabs-window-item>
						<v-tabs-window-item value="user">
							<v-data-table
								:headers="hoverHeaders"
								:items="userHovers"
								:loading="loading"
								density="comfortable"
								class="elevation-0"
								no-data-text="No user hovers found"
								items-per-page="25"
								hover
							>
								<template #top>
									<v-toolbar flat>
										<v-toolbar-title>User Hovers</v-toolbar-title>
										<v-divider class="mx-4" inset vertical />
										<v-chip class="mr-2">{{ userHovers.length }} records</v-chip>
										<m-btn color="primary" prepend-icon="mdi-refresh" @click="searchUserHoversForCurrentUser">Refresh</m-btn>
									</v-toolbar>
								</template>
							</v-data-table>
						</v-tabs-window-item>
					</v-tabs-window>
				</v-card>
			</v-col>
		</v-row>

		<!-- Add Hover Dialog -->
		<v-dialog v-model="showAddDialog" max-width="900" persistent>
			<v-card>
				<v-card-title>Add Hover Option</v-card-title>
				<v-card-text>
					<v-row>
						<v-col cols="12" md="6">
							<v-text-field v-model="newHover.funcCode" label="Function Code *" variant="outlined" density="comfortable" maxlength="100" :rules="[v => !!v || 'Required']" />
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field v-model="newHover.funcDesc" label="Description" variant="outlined" density="comfortable" maxlength="50" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" md="6">
							<v-text-field v-model="newHover.rclkUrl" label="URL" variant="outlined" density="comfortable" maxlength="256" />
						</v-col>
						<v-col cols="12" md="6">
						<v-text-field
							v-model="newHover.rclkReqst"
							label="Request Code"
							variant="outlined"
							density="comfortable"
							maxlength="32"
							:loading="loadingReqCodeValidation"
							:error="reqCodeValid === false"
							:hint="reqCodeValid === false ? 'Invalid request code' : reqCodeValid === true ? 'Valid ✓' : ''"
							persistent-hint
						/>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" md="3">
							<v-select v-model="newHover.polStat" :items="policyStatuses" item-title="text" item-value="value" label="Policy Status" variant="outlined" density="comfortable" />
						</v-col>
						<v-col cols="12" md="3">
							<v-select v-model="newHover.recStat" :items="recordStatuses" item-title="text" item-value="value" label="Record Status" variant="outlined" density="comfortable" />
						</v-col>
						<v-col cols="12" md="6">
							<v-select v-model="newHover.activityTypes" :items="activityTypes" item-title="text" item-value="value" label="Activity Types *" variant="outlined" density="comfortable" multiple chips />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" md="6">
							<v-select v-model="newHover.keyFields" :items="keyFields" item-title="description" item-value="code" label="Key Fields" variant="outlined" density="comfortable" multiple chips :loading="loadingKeyFields" />
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field v-model="newHover.userName" label="User Name (blank=default)" variant="outlined" density="comfortable" maxlength="10" />
						</v-col>
					</v-row>

					<!-- Default Override Checkboxes -->
					<v-row>
						<v-col cols="12">
							<span class="text-subtitle-2">Default Overrides:</span>
						</v-col>
						<v-col cols="auto"><v-checkbox v-model="newHover.defaultLoc" label="Location = 99" density="comfortable" hide-details /></v-col>
						<v-col cols="auto"><v-checkbox v-model="newHover.defaultMco" label="Master Co = 99" density="comfortable" hide-details /></v-col>
						<v-col cols="auto"><v-checkbox v-model="newHover.defaultPco" label="Policy Co = 99" density="comfortable" hide-details /></v-col>
						<v-col cols="auto"><v-checkbox v-model="newHover.defaultState" label="State = US" density="comfortable" hide-details /></v-col>
						<v-col cols="auto"><v-checkbox v-model="newHover.defaultLob" label="LOB = All" density="comfortable" hide-details /></v-col>
					</v-row>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn @click="showAddDialog = false" text="Cancel" />
					<m-btn color="primary" @click="submitAddHover" text="Add Hover" :disabled="!newHover.funcCode || newHover.activityTypes.length === 0" />
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script lang="ts">
import { ref, computed, onMounted, watch, inject } from 'vue';
import { useTableMaintenanceApi } from '@/composables/UseTableMaintenanceApi';
import { useCurrentUser } from '@/composables/UseCurrentUser';
import type { CompanyItem, StateItem, LobItem, IssueCodeItem, KeyFieldItem, HoverOption } from '@/models/TableMaintenanceViewModels';
import MBtn from '@/components/BaseButton.vue';
import WaitDialog from '@/components/WaitDialog.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'HoverManagement',
	components: { MBtn, WaitDialog },
	route: {
		path: '/SSAM/hovers',
		meta: {
			requiresAuth: true,
			title: 'Hover Management',
		},
	},

	setup() {
		const api = useTableMaintenanceApi();
		const tell = inject('tell') as Tell;
		const { getCurrentUser } = useCurrentUser();

		// ── State ──────────────────────────────────────────────────────────
		const loading = ref(false);
		const loadingCompanies = ref(false);
		const loadingStates = ref(false);
		const loadingLobs = ref(false);
		const loadingKeyFields = ref(false);
		const hasSearched = ref(false);

		const companies = ref<CompanyItem[]>([]);
		const states = ref<StateItem[]>([]);
		const lobs = ref<LobItem[]>([]);
		const issueCodes = ref<IssueCodeItem[]>([]);
		const keyFields = ref<KeyFieldItem[]>([]);
		const hovers = ref<HoverOption[]>([]);

		const selectedCompany = ref<CompanyItem | null>(null);
		const selectedState = ref<string | null>(null);
		const selectedLob = ref<string | null>(null);
		const selectedIssueCode = ref<string | null>(null);

		const showAddDialog = ref(false);

		const activeHoverTab = ref<'default' | 'user'>('default');
		const userHovers = ref<HoverOption[]>([]);
		const reqCodeValid = ref<boolean | null>(null);
		const loadingReqCodeValidation = ref(false);

		const newHover = ref({
			funcCode: '',
			funcDesc: '',
			rclkUrl: '',
			rclkReqst: '',
			polStat: '',
			recStat: '',
			activityTypes: [] as string[],
			keyFields: [] as string[],
			userName: '',
			defaultLoc: false,
			defaultMco: false,
			defaultPco: false,
			defaultState: false,
			defaultLob: false,
		});

		// ── Static data ────────────────────────────────────────────────────
		const policyStatuses = [
			{ text: '(Any)', value: '' },
			{ text: 'E - Errored', value: 'E' },
			{ text: 'D - Drop Pending', value: 'D' },
			{ text: 'H - History', value: 'H' },
			{ text: 'P - Pending', value: 'P' },
			{ text: 'V - Verified', value: 'V' },
		];

		const recordStatuses = [
			{ text: '(Any)', value: '' },
			{ text: 'D - Drop Pending', value: 'D' },
			{ text: 'H - History', value: 'H' },
			{ text: 'P - Pending', value: 'P' },
			{ text: 'V - Verified', value: 'V' },
		];

		const activityTypes = [
			{ text: 'NB - New Business', value: 'NB' },
			{ text: 'EN - Endorsement', value: 'EN' },
			{ text: 'RN - Renewal', value: 'RN' },
			{ text: 'CN - Cancellation', value: 'CN' },
			{ text: 'RI - Reinstatement', value: 'RI' },
			{ text: 'AU - Audit', value: 'AU' },
		];

		const hoverHeaders = [
			{ title: 'Activity Type', key: 'actType', sortable: true },
			{ title: 'Function Code', key: 'funcCode', sortable: true },
			{ title: 'Description', key: 'funcDesc', sortable: true },
			{ title: 'Request Code', key: 'rclkReqst', sortable: true },
			{ title: 'URL', key: 'rclkUrl', sortable: true },
			{ title: 'Policy Status', key: 'polStat', sortable: true, width: 100 },
			{ title: 'Record Status', key: 'recStat', sortable: true, width: 100 },
			{ title: 'LOC', key: 'loc', sortable: true, width: 60 },
			{ title: 'MCO', key: 'mco', sortable: true, width: 60 },
			{ title: 'PCO', key: 'pco', sortable: true, width: 60 },
			{ title: 'State', key: 'state', sortable: true, width: 70 },
		];

		// ── Computed ───────────────────────────────────────────────────────
		const canSearch = computed(() =>
			selectedCompany.value && selectedState.value && selectedLob.value && selectedIssueCode.value
		);

		function companyDisplay(item: CompanyItem) {
			return `${item.loc}${item.mco}${item.pco} - ${item.companyName}`;
		}

		// ── Data loading ───────────────────────────────────────────────────
		async function loadCompanies() {
			loadingCompanies.value = true;
			try {
				companies.value = await api.getCompanies();
			} catch (e: unknown) {
				tell.error(`Error loading companies: ${(e as Error).message}`);
			} finally {
				loadingCompanies.value = false;
			}
		}

		async function onCompanyChange() {
			selectedState.value = null;
			selectedLob.value = null;
			selectedIssueCode.value = null;
			states.value = [];
			lobs.value = [];

			if (!selectedCompany.value) return;
			loadingStates.value = true;
			try {
				states.value = await api.getStates(
					selectedCompany.value.loc, selectedCompany.value.mco, selectedCompany.value.pco);
			} catch (e: unknown) {
				tell.error(`Error loading states: ${(e as Error).message}`);
			} finally {
				loadingStates.value = false;
			}
		}

		async function onStateChange() {
			selectedLob.value = null;
			selectedIssueCode.value = null;
			lobs.value = [];

			if (!selectedState.value || !selectedCompany.value) return;
			loadingLobs.value = true;
			try {
				lobs.value = await api.getLobs(
					selectedCompany.value.loc, selectedCompany.value.mco,
					selectedCompany.value.pco, selectedState.value);
			} catch (e: unknown) {
				tell.error(`Error loading LOBs: ${(e as Error).message}`);
			} finally {
				loadingLobs.value = false;
			}
		}

		async function onLobChange() {
			selectedIssueCode.value = null;
			await loadKeyFields();
		}

		async function loadKeyFields() {
			if (!selectedCompany.value || !selectedState.value || !selectedLob.value) return;
			loadingKeyFields.value = true;
			try {
				keyFields.value = await api.getKeyFields(
					selectedCompany.value.loc, selectedCompany.value.mco, selectedCompany.value.pco,
					selectedState.value, selectedLob.value, 'insuranceLine');
			} catch (e: unknown) {
				tell.error(`Error loading key fields: ${(e as Error).message}`);
			} finally {
				loadingKeyFields.value = false;
			}
		}

		async function searchHovers() {
			if (!canSearch.value || !selectedCompany.value) return;
			loading.value = true;
			hasSearched.value = true;
			try {
				hovers.value = await api.searchHovers({
					loc: selectedCompany.value.loc,
					mco: selectedCompany.value.mco,
					pco: selectedCompany.value.pco,
					state: selectedState.value!,
					lob: selectedLob.value!,
					issueCode: selectedIssueCode.value!,
				});
			} catch (e: unknown) {
				tell.error(`Error searching hovers: ${(e as Error).message}`);
				hovers.value = [];
			} finally {
				loading.value = false;
			}
		}

		async function searchUserHoversForCurrentUser() {
			if (!canSearch.value || !selectedCompany.value) return;
			loading.value = true;
			try {
				const userName = await getCurrentUser();
				userHovers.value = await api.searchUserHovers({
					loc: selectedCompany.value.loc,
					mco: selectedCompany.value.mco,
					pco: selectedCompany.value.pco,
					state: selectedState.value!,
					lob: selectedLob.value!,
					issueCode: selectedIssueCode.value!,
					userName,
				});
			} catch (e: unknown) {
				tell.error(`Error searching user hovers: ${(e as Error).message}`);
				userHovers.value = [];
			} finally {
				loading.value = false;
			}
		}

		// W3: Load user hovers when tab switches to 'user'
		watch(activeHoverTab, async (tab) => {
			if (tab === 'user' && canSearch.value) {
				await searchUserHoversForCurrentUser();
			}
		});

		// W3: Validate request code with debounce when user types in the Add Hover dialog
		let reqCodeTimeout: ReturnType<typeof setTimeout> | null = null;
		watch(() => newHover.value.rclkReqst, (code, _prev, onCleanup) => {
			reqCodeValid.value = null;
			if (reqCodeTimeout) {
				clearTimeout(reqCodeTimeout);
				reqCodeTimeout = null;
			}
			if (!code) {
				return;
			}
			reqCodeTimeout = setTimeout(async () => {
				loadingReqCodeValidation.value = true;
				try {
					const result = await api.validateRequestCode(code);
					reqCodeValid.value = result.isValid;
					if (!result.isValid) tell.warning(`Request code "${code}" is not recognized.`);
				} catch (e: unknown) {
					tell.error(`Error validating request code: ${(e as Error).message}`);
				} finally {
					loadingReqCodeValidation.value = false;
				}
			}, 600);
			onCleanup(() => {
				if (reqCodeTimeout) {
					clearTimeout(reqCodeTimeout);
					reqCodeTimeout = null;
				}
			});
		});

		async function submitAddHover() {
			if (!selectedCompany.value || !newHover.value.funcCode || newHover.value.activityTypes.length === 0) return;

			// W3: Check how many records would be affected before proceeding
			try {
				const impact = await api.getHoverImpact({
					loc: selectedCompany.value.loc,
					mco: selectedCompany.value.mco,
					pco: selectedCompany.value.pco,
					state: selectedState.value || '',
					lob: selectedLob.value || '',
					issueCode: selectedIssueCode.value || '',
					variant: newHover.value.funcCode,
					oldValue: '',
				});
				if (impact.affectedCount > 0 && !window.confirm(
					`This hover overlaps with ${impact.affectedCount} existing record(s). Proceed?`)) {
					return;
				}
			} catch {
				// Non-fatal — proceed even if impact check is unavailable
			}

			loading.value = true;
			try {
				const user = await getCurrentUser(); // W6
				const result = await api.addHover({
					user,
					fileName: 'PMSP0200',
					loc: selectedCompany.value.loc,
					mco: selectedCompany.value.mco,
					pco: selectedCompany.value.pco,
					issueCode: selectedIssueCode.value || ' ',
					state: selectedState.value || '',
					sumDetail: 'S',
					lob: selectedLob.value || '',
					funcCode: newHover.value.funcCode,
					funcDesc: newHover.value.funcDesc,
					rclkUrl: newHover.value.rclkUrl,
					rclkReqst: newHover.value.rclkReqst,
					polStat: newHover.value.polStat,
					recStat: newHover.value.recStat,
					activityTypes: newHover.value.activityTypes,
					keyFields: newHover.value.keyFields,
					keyFld2: '',
					keyFld3: '',
					keyFld4: '',
					keyFld5: '',
					userName: newHover.value.userName,
					coverageLevel: '',
					defaultLoc: newHover.value.defaultLoc,
					defaultMco: newHover.value.defaultMco,
					defaultPco: newHover.value.defaultPco,
					defaultState: newHover.value.defaultState,
					defaultLob: newHover.value.defaultLob,
				});

				if (result.errorCount === 0) {
					tell.success(`Successfully added ${result.successCount} hover(s).`);
					showAddDialog.value = false;
					resetNewHover();
					await searchHovers();
				} else {
					tell.error(`${result.errorCount} error(s): ${result.errors.join('; ')}`);
				}
			} catch (e: unknown) {
				tell.error(`Error adding hover: ${(e as Error).message}`);
				showAddDialog.value = false; // W7: close dialog so user isn't trapped
			} finally {
				loading.value = false;
			}
		}

		function resetNewHover() {
			newHover.value = {
				funcCode: '', funcDesc: '', rclkUrl: '', rclkReqst: '',
				polStat: '', recStat: '', activityTypes: [], keyFields: [],
				userName: '', defaultLoc: false, defaultMco: false,
				defaultPco: false, defaultState: false, defaultLob: false,
			};
		}

		// ── Lifecycle ──────────────────────────────────────────────────────
		onMounted(async () => {
			await Promise.all([
				loadCompanies(),
				api.getIssueCodes().then(r => issueCodes.value = r)
					.catch((e: unknown) => tell.error(`Error loading issue codes: ${(e as Error).message}`)),
			]);
		});

		return {
			loading, loadingCompanies, loadingStates, loadingLobs, loadingKeyFields,
			hasSearched, companies, states, lobs, issueCodes, keyFields, hovers,
			selectedCompany, selectedState, selectedLob, selectedIssueCode,
			showAddDialog, newHover,
			policyStatuses, recordStatuses, activityTypes, hoverHeaders,
			canSearch, companyDisplay,
			onCompanyChange, onStateChange, onLobChange,
			searchHovers, submitAddHover,
			activeHoverTab, userHovers, reqCodeValid, loadingReqCodeValidation,
			searchUserHoversForCurrentUser,
		};
	},
};
</script>
